/**
 * useDragPosition — SSOT for the MentalityFab's drag position.
 *
 * Owns the snapshot/restore lifecycle:
 *   - Hydrates from localStorage on mount.
 *   - Persists on drag-end (NOT during drag — would thrash storage).
 *   - Re-clamps on viewport resize so a previously-valid position
 *     never strands the FAB outside the viewport.
 *
 * Storage key: `chiefy.mentality.fab.position` → `{ x: number, y: number }`.
 * Both coordinates are pixel offsets from the viewport's top-left
 * corner, addressing the FAB's own top-left.
 *
 * Why not the existing edge+ratio snapshot?
 *   The original MentalityFab modelled position as an edge ('left' |
 *   'right' | 'top' | 'bottom') + a ratio along that edge. That model
 *   coupled persistence to the snap-to-edge behaviour and made the
 *   stored snapshot opaque (you couldn't tell where the FAB lived
 *   without re-running the anchor math).
 *
 *   This hook decouples persistence from snap-to-edge: callers persist
 *   the post-snap `{x, y}` and the snap logic lives where it belongs
 *   — inside MentalityFab's pointer handler. Storage is now self-
 *   describing, debuggable, and trivially restorable across reloads.
 *
 * Safety
 * ------
 * - localStorage parse failures route through `captureError` SSOT.
 * - Resize-clamps with `INSET` so a remembered (1900, 100) doesn't
 *   strand the FAB just off-screen on a narrower window.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { captureError } from '../../../core/errors/captureError';

// eslint-disable-next-line chiefy/no-naked-concept-strings -- localStorage key (infrastructure id), not user copy. Must stay stable across reloads.
export const FAB_POSITION_STORAGE_KEY = 'chiefy.mentality.fab.position';

// sessionStorage gate so the QuotaExceeded toast surfaces at most
// once per browser session. The drag-end path runs on every release —
// without this gate a Safari-private-mode user would see a toast on
// every interaction. Storing the gate in sessionStorage (not module
// state) means a hard reload re-arms the toast in case storage came
// back, while a soft re-render does not.
// eslint-disable-next-line chiefy/no-naked-concept-strings -- sessionStorage key (infrastructure id), not user copy.
export const FAB_QUOTA_TOAST_SHOWN_KEY = 'chiefy.mentality.fab.quota-toast-shown';

/** Detect QuotaExceededError across UAs. Safari's private mode throws
 *  a DOMException with name === 'QuotaExceededError', Firefox/Chrome
 *  use the same name, and the legacy code 22 / 1014 fallbacks cover
 *  pre-DOM-Level-4 paths. Kept defensive — never throws. */
function isQuotaExceeded(err: unknown): boolean {
    if (err === null || typeof err !== 'object') return false;
    const candidate = err as { name?: unknown; code?: unknown };
    if (candidate.name === 'QuotaExceededError') return true;
    if (candidate.name === 'NS_ERROR_DOM_QUOTA_REACHED') return true;
    if (candidate.code === 22 || candidate.code === 1014) return true;
    return false;
}

/** Pixel inset from the viewport edge when clamping. Matches the
 *  20px canvas inset used elsewhere; using 16px here gives a touch
 *  more visual breathing room when a remembered position would
 *  otherwise sit flush against a window edge. */
export const FAB_POSITION_CLAMP_INSET = 16;

export type DragPosition = { x: number; y: number };

type Viewport = { width: number; height: number };

type UseDragPositionArgs = {
    /** Used as a fallback when nothing is persisted. */
    defaultPosition: DragPosition;
    /** Pixel dimensions of the draggable element — used to size the
     *  clamp envelope. The FAB is 44×44 today; passing the size keeps
     *  the hook agnostic of any specific element. */
    elementSize: { width: number; height: number };
    /** Optional callback fired with user-facing copy when persistence
     *  rejects with a QuotaExceededError (Safari private mode, full
     *  storage). Decouples the hook from the toast context so it
     *  remains usable from any tree — the consumer is responsible for
     *  wiring the toast SSOT. Throttled to one-per-session via
     *  `chiefy.mentality.fab.quota-toast-shown` in sessionStorage. */
    onPersistQuotaExceeded?: (message: string) => void;
};

function readViewport(): Viewport {
    if (typeof window === 'undefined') return { width: 0, height: 0 };
    return { width: window.innerWidth, height: window.innerHeight };
}

function clamp(n: number, lo: number, hi: number): number {
    return Math.max(lo, Math.min(hi, n));
}

/**
 * Clamp `(x, y)` so the element of size `elementSize` stays inside
 * `viewport` with the configured edge inset. If the viewport is
 * smaller than the element (degenerate case during a teardown), we
 * still return finite numbers — never NaN — so React state stays sane.
 */
export function clampToViewport(
    position: DragPosition,
    elementSize: { width: number; height: number },
    viewport: Viewport,
    inset: number = FAB_POSITION_CLAMP_INSET,
): DragPosition {
    const maxX = Math.max(inset, viewport.width - elementSize.width - inset);
    const maxY = Math.max(inset, viewport.height - elementSize.height - inset);
    return {
        x: clamp(position.x, inset, maxX),
        y: clamp(position.y, inset, maxY),
    };
}

function loadPersisted(): DragPosition | null {
    if (typeof window === 'undefined') return null;
    try {
        const raw = window.localStorage.getItem(FAB_POSITION_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Partial<DragPosition>;
        if (
            typeof parsed.x === 'number' && Number.isFinite(parsed.x)
            && typeof parsed.y === 'number' && Number.isFinite(parsed.y)
        ) {
            return { x: parsed.x, y: parsed.y };
        }
        return null;
    } catch (err) {
        captureError(err, {
            scope: 'useDragPosition', op: 'loadPersisted',
        });
        return null;
    }
}

/** User-facing copy surfaced when localStorage rejects the write (most
 *  commonly Safari private mode hitting QuotaExceededError). Plain
 *  string — toast container is the only consumer — so no GLOSSARY
 *  routing is required. The copy is a one-shot fallback ("Position
 *  couldn't be saved — drag again to retry."), not a concept noun. */
export const FAB_QUOTA_TOAST_MESSAGE =
    "Position couldn't be saved — drag again to retry.";

/** Optional toast emitter wired by the hook. Decoupled from `writePersisted`
 *  so the persistence path stays callable from non-React seams (legacy
 *  tests, future SSR snapshot) without dragging the toast context in. */
type QuotaToastEmitter = (message: string) => void;

function writePersisted(
    position: DragPosition,
    onQuotaExceeded?: QuotaToastEmitter,
): void {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(
            FAB_POSITION_STORAGE_KEY,
            JSON.stringify(position),
        );
    } catch (err) {
        captureError(err, {
            scope: 'useDragPosition', op: 'writePersisted',
        });
        // QuotaExceeded defensive guard: Safari private mode and any
        // other UA at its storage cap reject the write. Surface a
        // one-time toast so the user knows the drag didn't stick —
        // throttled via sessionStorage so the same session never
        // double-fires.
        if (!isQuotaExceeded(err) || !onQuotaExceeded) return;
        try {
            const alreadyShown =
                window.sessionStorage.getItem(FAB_QUOTA_TOAST_SHOWN_KEY) === '1';
            if (alreadyShown) return;
            window.sessionStorage.setItem(FAB_QUOTA_TOAST_SHOWN_KEY, '1');
            onQuotaExceeded(FAB_QUOTA_TOAST_MESSAGE);
        } catch (gateErr) {
            // Why: if sessionStorage itself is also unavailable (rare —
            // private-mode UAs typically reject only one or the other
            // but covering both keeps the path symmetric), still
            // surface the toast so the user sees feedback. The throttle
            // degrades to a per-mount best-effort.
            captureError(gateErr, {
                scope: 'useDragPosition', op: 'quotaToastGate',
            });
            onQuotaExceeded(FAB_QUOTA_TOAST_MESSAGE);
        }
    }
}

/**
 * Hook contract:
 *   - `position`        — current `{x, y}` to render at.
 *   - `setPosition`     — update during drag (does NOT persist).
 *   - `commitPosition`  — call on drag-end. Clamps + persists + sets.
 *
 * Internally the hook re-clamps on viewport resize so the FAB never
 * strands outside the visible area after a window resize.
 */
export function useDragPosition({
    defaultPosition,
    elementSize,
    onPersistQuotaExceeded,
}: UseDragPositionArgs) {
    // Hydrate once on mount. Reading inside the initializer means we
    // never render the default on first paint if storage has a value.
    const [position, setPositionState] = useState<DragPosition>(() => {
        const viewport = readViewport();
        const persisted = loadPersisted();
        const seed = persisted ?? defaultPosition;
        return clampToViewport(seed, elementSize, viewport);
    });

    // Track the element size in a ref so the resize handler doesn't
    // capture stale closures across renders. The ref-write happens
    // inside an effect (not at render time) to satisfy the
    // react-hooks/refs-in-render lint — readers (the resize handler)
    // run inside an effect or event so they observe the updated value.
    const elementSizeRef = useRef(elementSize);
    useEffect(() => {
        elementSizeRef.current = elementSize;
    }, [elementSize]);

    // Hold the quota-toast callback in a ref so `commitPosition` (a
    // stable identity) reads the latest emitter without re-binding on
    // every render. Keeps the hook decoupled from any specific toast
    // context implementation.
    const quotaCallbackRef = useRef(onPersistQuotaExceeded);
    useEffect(() => {
        quotaCallbackRef.current = onPersistQuotaExceeded;
    }, [onPersistQuotaExceeded]);

    const setPosition = useCallback((next: DragPosition) => {
        setPositionState(next);
    }, []);

    const commitPosition = useCallback((next: DragPosition) => {
        const viewport = readViewport();
        const clamped = clampToViewport(next, elementSizeRef.current, viewport);
        setPositionState(clamped);
        writePersisted(clamped, quotaCallbackRef.current);
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const onResize = () => {
            const viewport = readViewport();
            setPositionState((prev) => {
                const clamped = clampToViewport(
                    prev, elementSizeRef.current, viewport,
                );
                if (clamped.x === prev.x && clamped.y === prev.y) return prev;
                return clamped;
            });
        };
        window.addEventListener('resize', onResize);
        window.addEventListener('orientationchange', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
            window.removeEventListener('orientationchange', onResize);
        };
    }, []);

    return { position, setPosition, commitPosition };
}
